import {
  addDaysYmd,
  CHANNEL_COLOR,
  CHANNEL_LABEL,
  CHANNEL_ORDER,
  formatMD,
  isOverdue,
  isTimelineMarkTag,
  markColor,
  nowJSTTime,
  STATUS_COLOR,
  STATUS_LABEL,
  TASK_TAG_LABEL,
  weekdayJa,
} from "../../lib/mkRules";
import type { Initiative, InitiativeChannel, InitiativeType, Task } from "../../types/contract";
import { SourceBadge } from "./SourceBadge";

export type ChannelOrAll = "all" | InitiativeChannel;
export type TypeOrAll = "all" | InitiativeType;
export type TimelineRange = "2w" | "m";

interface TimelineProps {
  initiatives: Initiative[];
  tasks: Task[];
  today: string;
  range: TimelineRange;
  onRangeChange: (r: TimelineRange) => void;
  channelFilter: ChannelOrAll;
  onChannelFilterChange: (c: ChannelOrAll) => void;
  typeFilter: TypeOrAll;
  onTypeFilterChange: (t: TypeOrAll) => void;
  onOpen: (id: string) => void;
}

function daysInMonth(y: number, m: number): number {
  return new Date(Date.UTC(y, m, 0)).getUTCDate();
}

function tint(color: string): string {
  return `${color}14`;
}

const TYPE_LABEL: Record<InitiativeType, string> = { recurring: "定期", oneoff: "単発" };
const CHANNEL_CHIPS: ChannelOrAll[] = ["all", ...CHANNEL_ORDER];

export function Timeline({
  initiatives,
  tasks,
  today,
  range,
  onRangeChange,
  channelFilter,
  onChannelFilterChange,
  typeFilter,
  onTypeFilterChange,
  onOpen,
}: TimelineProps) {
  let start: string;
  let days: number;
  let rangeLabel: string;
  if (range === "2w") {
    start = addDaysYmd(today, -6);
    days = 14;
    rangeLabel = `${formatMD(start)} — ${formatMD(addDaysYmd(start, 13))}`;
  } else {
    const [y, m] = today.split("-").map((v) => parseInt(v, 10));
    start = `${y}-${String(m).padStart(2, "0")}-01`;
    days = daysInMonth(y, m);
    rangeLabel = `${y}年 ${m}月`;
  }
  const end = addDaysYmd(start, days - 1);
  const gridCols = `236px repeat(${days}, minmax(28px, 1fr))`;

  const scale = Array.from({ length: days }, (_, i) => {
    const d = addDaysYmd(start, i);
    const wd = weekdayJa(d);
    const isToday = d === today;
    const color = isToday ? "#a8442a" : wd === "土" ? "#3a5fa8" : wd === "日" ? "#b3452f" : "#6d6a78";
    const label = range === "m" ? String(parseInt(d.split("-")[2], 10)) : `${formatMD(d)} ${wd}`;
    return { d, label, color, isToday };
  });

  const visible = initiatives.filter(
    (i) =>
      (channelFilter === "all" || i.channel === channelFilter) &&
      (typeFilter === "all" || i.type === typeFilter) &&
      i.end_date >= start &&
      i.start_date <= end
  );

  const nowTime = nowJSTTime();

  return (
    <div className="mk-timeline-col mk-desktop-only">
      <div className="mk-toolbar">
        <span className="mk-range-label">{rangeLabel}</span>
        <div className="mk-seg">
          <button className={range === "2w" ? "active" : ""} onClick={() => onRangeChange("2w")}>
            2週
          </button>
          <button className={range === "m" ? "active" : ""} onClick={() => onRangeChange("m")}>

            月
          </button>
        </div>
        <div className="mk-chip-row">
          {CHANNEL_CHIPS.map((c) => {
            const on = channelFilter === c;
            const color = c === "all" ? "#55525f" : CHANNEL_COLOR[c];
            const style = on
              ? { background: color, color: "#f7f7f5", borderColor: color }
              : { background: tint(color), color, borderColor: `${color}55` };
            return (
              <button key={c} className="mk-chip" style={style} onClick={() => onChannelFilterChange(c)}>
                {c === "all" ? "すべて" : CHANNEL_LABEL[c]}
              </button>
            );
          })}
        </div>
        <div className="mk-seg" style={{ marginLeft: "auto" }}>
          {(["all", "recurring", "oneoff"] as TypeOrAll[]).map((t) => (
            <button key={t} className={typeFilter === t ? "active" : ""} onClick={() => onTypeFilterChange(t)}>
              {t === "all" ? "すべて" : TYPE_LABEL[t]}
            </button>
          ))}
        </div>
      </div>

      <div className="mk-grid-wrap">
        <div className="mk-grid">
          <div style={{ display: "grid", gridTemplateColumns: gridCols }}>
            <div className="mk-grid-head-name">施策 ／ ステータス</div>
            {scale.map((s) => (
              <div
                key={s.d}
                className="mk-grid-head-day"
                style={{ color: s.color, background: s.isToday ? "#fdf3f0" : "transparent" }}
              >
                {s.label}
              </div>
            ))}
          </div>

          {visible.length === 0 && <div className="mk-grid-empty">この条件に一致する施策はありません</div>}

          {visible.map((init) => {
            const color = CHANNEL_COLOR[init.channel];
            const barStart = init.start_date > start ? init.start_date : start;
            // end_open（終了日未定・継続中）は表示範囲の右端まで帯を伸ばす（plan-mk2.md §3）。
            const barEnd = init.end_open ? end : init.end_date < end ? init.end_date : end;
            const startIdx = scale.findIndex((s) => s.d === barStart);
            const endIdx = scale.findIndex((s) => s.d === barEnd);
            const s0 = startIdx < 0 ? 0 : startIdx;
            const s1 = endIdx < 0 ? days - 1 : endIdx;
            const span = Math.max(1, s1 - s0 + 1);
            const marks = tasks.filter(
              (t) =>
                t.initiative_id === init.id &&
                !t.done &&
                isTimelineMarkTag(t.tag) &&
                t.due_date >= start &&
                t.due_date <= end
            );
            return (
              <div
                key={init.id}
                style={{
                  display: "grid",
                  gridTemplateColumns: gridCols,
                  gridTemplateRows: "30px 18px",
                  background: init.status === "needs_action" ? "#fdf3f0" : "transparent",
                }}
              >
                <div
                  className="mk-grid-row-name"
                  style={{ gridColumn: "1 / 2", gridRow: "1 / 3" }}
                  onClick={() => onOpen(init.id)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onOpen(init.id);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                >
                  <div>
                    <span className="mk-ch-bar" style={{ background: color }} />
                    <span className="mk-init-name">{init.name}</span>
                  </div>
                  <div className="mk-init-meta">
                    <span style={{ color: STATUS_COLOR[init.status] }}>{STATUS_LABEL[init.status]}</span>
                    <span className="mk-type-chip">{TYPE_LABEL[init.type]}</span>
                    <SourceBadge source={init.source} channel={init.channel} />
                  </div>
                </div>
                <div
                  className="mk-bar"
                  style={{
                    gridColumn: `${s0 + 2} / span ${span}`,
                    gridRow: "1 / 2",
                    background: init.status === "done" ? "#f0efec" : tint(color),
                    borderLeftColor: color,
                    borderTop: `1px solid ${color}44`,
                    borderRight: `1px solid ${color}44`,
                    borderBottom: `1px solid ${color}44`,
                  }}
                  onClick={() => onOpen(init.id)}
                >
                  {init.bar_note ?? ""}
                  {init.end_open && (
                    <span className="mk-bar-open-tag" title="終了日未定（継続中）">
                      継続中
                    </span>
                  )}
                </div>
                {marks.map((t) => {
                  const idx = scale.findIndex((s) => s.d === t.due_date);
                  if (idx < 0) return null;
                  const overdue = isOverdue(t.due_date, t.due_time, today, nowTime);
                  return (
                    <span
                      key={t.id}
                      className="mk-mark"
                      style={{
                        gridColumn: `${idx + 2} / span 1`,
                        gridRow: "2 / 3",
                        background: markColor(t.tag, overdue),
                      }}
                    >
                      {TASK_TAG_LABEL[t.tag]}
                    </span>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
      <p className="mk-hint">
        行をクリックすると施策詳細を開きます。帯は施策の期間、下のラベルはレビュー・切替・終了処理の日。
      </p>
    </div>
  );
}
